import React, { useState } from 'react';
import { Box, Dialog, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CouponMasterItemTableRow, CouponPackMasterFormInput } from '../../../types/couponPackMaster';
import { theme } from '../../../theme';
import { PackSmartCheckTemplate } from './components/PackSmartCheckTemplate';
import { PackDetailInputSmartCheckTemplate } from './components/PackDetailInputSmartCheckTemplate';
import { PackCompletedSmartCheckTemplate } from './components/PackCompletedSmartCheckTemplate';
import { HtmlText } from '@sbgift-coupon/use';

export type CouponConsumerDisplayPreviewProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  input: CouponPackMasterFormInput;
  logoImageUrl?: string;
  couponImageUrl?: string;
};

const useStyles = makeStyles(() => ({
  paper: {
    height: '100%',
    maxWidth: '417px',
    width: '100%',
    position: 'relative',
  },
  header: {
    display: 'flex',
    justifyContent: 'center',
    padding: '20px',
    maxHeight: '60px',
    top: 0,
    backgroundColor: '#FFFFFF',
    width: '100%',
    zIndex: 1000,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  },
  headerChildren: {
    maxWidth: theme.breakpoints.values.lg,
  },
  footerChildren: {
    display: 'flex',
    flexDirection: 'column',
    gap: 26,
    justifyContent: 'center',
    margin: '0 auto',
    maxWidth: '768px',
    padding: '40px 20px',
    boxSizing: 'border-box',
  },
  footer: {
    marginTop: 'auto',
    backgroundColor: '#909090',
  },
  label: {
    color: theme.palette.common.white,
    fontWeight: 600,
    fontSize: '14px',
  },
  contentFooter: {
    color: theme.palette.common.white,
    fontWeight: 300,
    fontSize: '14px',
    lineHeight: 1.6,
  },
}));

export const CouponPackMasterSmartCheckDisplayPreview: React.FC<CouponConsumerDisplayPreviewProps> = (props) => {
  const { open, setOpen, input } = props;
  const classes = useStyles();
  // status has value is 'LIST' | 'INPUT' | 'CONFIRMED' | 'COMPLETED
  const [status, setStatus] = useState<string>('LIST');
  const [currentCouponSelected, setCurrentCouponSelected] = useState<CouponMasterItemTableRow>();
  const [usedQuantity, setUsedQuantity] = useState<number>(1);
  const [codeInput, setCodeInput] = useState<string>('');

  return (
    <Dialog
      classes={{
        paper: classes.paper,
      }}
      onClose={() => setOpen(false)}
      open={open}
      keepMounted
    >
      {!!props.logoImageUrl && (
        <header className={classes.header}>
          <Grid container alignItems="center" spacing={2} className={classes.headerChildren}>
            <Grid item xs={6} container alignItems="center">
              <img
                src={props.logoImageUrl}
                alt="Logo"
                style={{ height: '19.32px' }}
                onClick={() => {
                  setStatus('LIST');
                }}
              />
            </Grid>
          </Grid>
        </header>
      )}
      {(() => {
        switch (status) {
          case 'LIST':
            // Render list view
            return (
              <PackSmartCheckTemplate
                open={open}
                input={input}
                couponImageUrl={props.couponImageUrl}
                logoImageUrl={props.logoImageUrl}
                setStatus={setStatus}
                setCurrentCouponSelected={setCurrentCouponSelected}
                displayCouponPackName={props.input.displayCouponName || props.input.couponName}
              />
            );
          case 'INPUT':
            // Render input view
            return (
              <PackDetailInputSmartCheckTemplate
                setStatus={setStatus}
                currentCouponSelected={currentCouponSelected}
                setUsedQuantity={setUsedQuantity}
                setCodeInput={setCodeInput}
              />
            );
          case 'COMPLETED':
            // Render completed view
            return (
              <PackCompletedSmartCheckTemplate
                setStatus={setStatus}
                currentCouponSelected={currentCouponSelected}
                usedQuantity={usedQuantity}
                codeInput={codeInput}
              />
            );
          default:
            return null;
        }
      })()}

      {(input?.description?.trim() || input?.note?.trim()) && status !== 'COMPLETED' && (
        <footer className={classes.footer}>
          <Box className={classes.footerChildren}>
            {input?.description?.trim() && (
              <Box className={classes.contentFooter}>
                <HtmlText text={input.description} />
              </Box>
            )}

            {input?.note?.trim() && (
              <Box>
                <Typography className={classes.label}>お問い合わせ先</Typography>
                <Box className={classes.contentFooter}>
                  <HtmlText text={input.note} />
                </Box>
              </Box>
            )}
          </Box>
        </footer>
      )}
    </Dialog>
  );
};
